import { CalibrationLevel } from 'libs/end-to-end/src/calibration-level';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { guardExhaustive } from 'libs/end-to-end/src/generic';

export interface CalibrationLevelUIDetail {
  level: CalibrationLevel;
  descriptionMessageId:
    | 'shortNone'
    | 'shortVeryLow'
    | 'shortLow'
    | 'shortLowToMed'
    | 'shortMed'
    | 'shortMedToHigh'
    | 'shortHigh'
    | 'shortVeryHigh'
    | 'shortHighest';
  specialDescriptionMessageId:
    | 'longNone'
    | 'longVeryLow'
    | 'longLow'
    | 'longLowToMed'
    | 'longMed'
    | 'longMedToHigh'
    | 'longHigh'
    | 'longVeryHigh'
    | 'longHighest';
  biasFramingDescriptorId:
    | 'biasNone'
    | 'biasVeryLow'
    | 'biasLow'
    | 'biasLowToMed'
    | 'biasMed'
    | 'biasMedToHigh'
    | 'biasHigh';
  optimizationFramingDescriptorId:
    | 'optimizationLow'
    | 'optimizationLowToMed'
    | 'optimizationMed'
    | 'optimizationMedToHigh'
    | 'optimizationHigh'
    | 'optimizationVeryHigh'
    | 'optimizationHighest';
  biasFramingWithSentenceDescriptorId:
    | 'biasOptimizationInSentenceNo'
    | 'longVeryLow'
    | 'longLow'
    | 'longLowToMed'
    | 'longMed'
    | 'longMedToHigh'
    | 'longHigh'
    | 'longVeryHigh'
    | 'longHighest';
  optimizationFramingWithSentenceDescriptorId:
    | 'biasOptimizationInSentenceNo'
    | 'longVeryLow'
    | 'longLow'
    | 'longLowToMed'
    | 'longMed'
    | 'longMedToHigh'
    | 'longHigh'
    | 'longVeryHigh'
    | 'longHighest';
  textColor: string;
  bgColor: string;
  hexColor: string;
  longerText: boolean;
}

export const getCalibrationFramingDescriptorId = ({
  calibrationFraming,
  calibrationLevel,
}: {
  calibrationFraming: CalibrationFraming;
  calibrationLevel: CalibrationLevel;
}) => {
  if (calibrationFraming === 'bias') {
    return getCalibrationLevelUIDetails(calibrationFraming)[calibrationLevel]
      .biasFramingDescriptorId;
  } else if (calibrationFraming === 'optimization') {
    return getCalibrationLevelUIDetails(calibrationFraming)[calibrationLevel]
      .optimizationFramingDescriptorId;
  } else {
    throw guardExhaustive(calibrationFraming);
  }
};

export const getCalibrationFramingWithSentenceDescriptorId = ({
  calibrationFraming,
  calibrationLevel,
}: {
  calibrationFraming: CalibrationFraming;
  calibrationLevel: CalibrationLevel;
}) => {
  if (calibrationFraming === 'bias') {
    return getCalibrationLevelUIDetails(calibrationFraming)[calibrationLevel]
      .biasFramingWithSentenceDescriptorId;
  } else if (calibrationFraming === 'optimization') {
    return getCalibrationLevelUIDetails(calibrationFraming)[calibrationLevel]
      .optimizationFramingWithSentenceDescriptorId;
  } else {
    throw guardExhaustive(calibrationFraming);
  }
};

export const getCalibrationLevelUIDetails = (
  calibrationFraming: CalibrationFraming,
): Record<CalibrationLevel, CalibrationLevelUIDetail> => {
  return {
    poor: {
      level: 'poor',
      descriptionMessageId:
        calibrationFraming === 'bias' ? 'shortHigh' : 'shortLow',
      specialDescriptionMessageId:
        calibrationFraming === 'bias' ? 'longHigh' : 'longLow',
      biasFramingDescriptorId: 'biasHigh',
      optimizationFramingDescriptorId: 'optimizationLow',
      biasFramingWithSentenceDescriptorId: 'longHigh',
      optimizationFramingWithSentenceDescriptorId: 'longLow',
      textColor: 'text-red-600',
      bgColor: 'bg-red-600',
      hexColor: '#D41515',
      longerText: false,
    },
    belowAverage: {
      level: 'belowAverage',
      descriptionMessageId:
        calibrationFraming === 'bias' ? 'shortMedToHigh' : 'shortLowToMed',
      specialDescriptionMessageId:
        calibrationFraming === 'bias' ? 'longMedToHigh' : 'longLowToMed',
      biasFramingDescriptorId: 'biasMedToHigh',
      optimizationFramingDescriptorId: 'optimizationLowToMed',
      biasFramingWithSentenceDescriptorId: 'longMedToHigh',
      optimizationFramingWithSentenceDescriptorId: 'longLowToMed',
      textColor: 'text-raspberry-700',
      bgColor: 'bg-raspberry-700',
      hexColor: '#A60C59',
      longerText: true,
    },
    average: {
      level: 'average',
      descriptionMessageId: 'shortMed',
      specialDescriptionMessageId: 'longMed',
      biasFramingDescriptorId: 'biasMed',
      optimizationFramingDescriptorId: 'optimizationMed',
      biasFramingWithSentenceDescriptorId: 'longMed',
      optimizationFramingWithSentenceDescriptorId: 'longMed',
      textColor: 'text-violet-700',
      bgColor: 'bg-violet-700',
      hexColor: '#791FD1',
      longerText: false,
    },
    good: {
      level: 'good',
      descriptionMessageId:
        calibrationFraming === 'bias' ? 'shortLowToMed' : 'shortMedToHigh',
      specialDescriptionMessageId:
        calibrationFraming === 'bias' ? 'longLowToMed' : 'longMedToHigh',
      biasFramingDescriptorId: 'biasLowToMed',
      optimizationFramingDescriptorId: 'optimizationMedToHigh',
      biasFramingWithSentenceDescriptorId: 'longLowToMed',
      optimizationFramingWithSentenceDescriptorId: 'longMedToHigh',
      textColor: 'text-purple-700',
      bgColor: 'bg-purple-700',
      hexColor: '#552BD3',
      longerText: true,
    },
    veryGood: {
      level: 'veryGood',
      descriptionMessageId:
        calibrationFraming === 'bias' ? 'shortLow' : 'shortHigh',
      specialDescriptionMessageId:
        calibrationFraming === 'bias' ? 'longLow' : 'longHigh',
      biasFramingDescriptorId: 'biasLow',
      optimizationFramingDescriptorId: 'optimizationHigh',
      biasFramingWithSentenceDescriptorId: 'longLow',
      optimizationFramingWithSentenceDescriptorId: 'longHigh',
      textColor: 'text-royal-700',
      bgColor: 'bg-royal-700',
      hexColor: '#1B47C9',
      longerText: false,
    },
    excellent: {
      level: 'excellent',
      descriptionMessageId:
        calibrationFraming === 'bias' ? 'shortVeryLow' : 'shortVeryHigh',
      specialDescriptionMessageId:
        calibrationFraming === 'bias' ? 'longVeryLow' : 'longVeryHigh',
      biasFramingDescriptorId: 'biasVeryLow',
      optimizationFramingDescriptorId: 'optimizationVeryHigh',
      biasFramingWithSentenceDescriptorId: 'longVeryLow',
      optimizationFramingWithSentenceDescriptorId: 'longVeryHigh',
      textColor: 'text-royal-700',
      bgColor: 'bg-royal-700',
      hexColor: '#1B47C9',
      longerText: true,
    },
    best: {
      level: 'best',
      descriptionMessageId:
        calibrationFraming === 'bias' ? 'shortNone' : 'shortHighest',
      specialDescriptionMessageId:
        calibrationFraming === 'bias' ? 'longNone' : 'longHighest',
      biasFramingDescriptorId: 'biasNone',
      optimizationFramingDescriptorId: 'optimizationHighest',
      biasFramingWithSentenceDescriptorId: 'biasOptimizationInSentenceNo',
      optimizationFramingWithSentenceDescriptorId: 'longHighest',
      textColor: 'text-royal-700',
      bgColor: 'bg-royal-700',
      hexColor: '#1B47C9',
      longerText: false,
    },
  };
};
